import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  FileText, 
  Upload, 
  ArrowLeft,
  BadgeCheck,
  XCircle,
  Eye,
  ChevronRight
} from 'lucide-react';

import professionalProfileApi from '../../api/professionalProfile';
import DocumentStatusCard from './components/DocumentStatusCard';
import VerificationArea from './components/VerificationArea';
import Button from '../../components/common/Button';
import { useQuery } from '@tanstack/react-query'
import documentsApi from '../../api/documents';

const VerificationStatusPage = () => {
  const navigate = useNavigate();
  const [verificationData, setVerificationData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Use React Query for document verifications
  const { 
    data: documentVerifications = [], 
    isLoading: documentLoading, 
    isError: isDocumentError, 
    error: documentError, 
    refetch: refetchDocumentVerifications 
  } = useQuery({
    queryKey: ['document-verifications'],
    queryFn: async () => {
      const res = await documentsApi.getDocumentVerifications();
      return res.data;
    }
  });

  useEffect(() => {
    fetchVerificationStatus();
  }, []);

  const fetchVerificationStatus = async () => {
    try {
      const response = await professionalProfileApi.getVerificationStatus();
      setVerificationData(response.data);
    } catch (error) {
      console.error('Failed to fetch verification status');
    } finally {
      setLoading(false);
    }
  };


  // Process document verifications to categorize them
  const processDocumentVerifications = () => {
    if (!documentVerifications || documentVerifications.length === 0) {
      return {
        verified_documents: [],
        pending_documents: [],
        rejected_documents: []
      };
    }

    const verified_documents = documentVerifications.filter(doc => doc.status === 'verified');
    const pending_documents = documentVerifications.filter(doc => doc.status === 'pending');
    const rejected_documents = documentVerifications.filter(doc => doc.status === 'rejected');

    return {
      verified_documents,
      pending_documents,
      rejected_documents
    };
  };

  const getOverallStatus = () => {
    const { 
      is_verified, 
      verification_percentage 
    } = verificationData || {};

    const { rejected_documents, pending_documents } = processDocumentVerifications();

    if (verification_percentage === 100) return { status: 'verified', message: 'Fully Verified' };
    if (rejected_documents.length > 0) return { status: 'rejected', message: 'Needs Attention' };
    if (pending_documents.length > 0 || verification_percentage > 0) return { status: 'pending', message: 'Under Review' };
    if (verification_percentage === 0) return { status: 'not-started', message: 'Not Started' };
    
    return { status: 'in-progress', message: 'In Progress' };
  };

  const getPrimaryAction = () => {
    const { 
      verification_percentage 
    } = verificationData || {};

    const { rejected_documents, pending_documents } = processDocumentVerifications();

    if (verification_percentage === 100) {
      return {
        label: 'View Documents',
        onClick: () => navigate('/manage-profile?tab=documents'),
        variant: 'outline',
        icon: Eye
      };
    }

    if (rejected_documents.length > 0) {
      return {
        label: 'Fix Issues',
        onClick: () => navigate('/manage-profile?tab=documents'),
        variant: 'primary',
        icon: AlertCircle
      };
    }

    if (pending_documents.length > 0) {
      return {
        label: 'Upload More Documents',
        onClick: () => navigate('/manage-profile?tab=documents'),
        variant: 'primary',
        icon: Upload
      };
    }

    return {
      label: 'Start Verification',
      onClick: () => navigate('/manage-profile?tab=documents'),
      variant: 'primary',
      icon: FileText
    };
  };

  if (loading || documentLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading verification status...</p>
        </div>
      </div>
    );
  }

  const {
    is_verified,
    verification_percentage,
    education_verified,
    employment_verified,
    identity_verified,
    licenses_verified,
    last_verification_date
  } = verificationData || {};

  const {
    verified_documents,
    pending_documents,
    rejected_documents
  } = processDocumentVerifications();

  const overallStatus = getOverallStatus();
  const primaryAction = getPrimaryAction();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="link"
            onClick={() => navigate(-1)}
            className="mb-4 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Profile Verification</h1>
              <p className="mt-2 text-gray-600">
                Track your verification status and document reviews
              </p>
            </div>
            
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${
              overallStatus.status === 'verified' 
                ? 'bg-green-100 text-green-800 border-green-200' 
                : overallStatus.status === 'rejected'
                ? 'bg-red-100 text-red-800 border-red-200'
                : overallStatus.status === 'pending'
                ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                : 'bg-blue-100 text-blue-800 border-blue-200'
            }`}>
              {overallStatus.status === 'verified' && <BadgeCheck className="w-5 h-5" />}
              {overallStatus.status === 'rejected' && <AlertCircle className="w-5 h-5" />}
              {overallStatus.status === 'pending' && <Clock className="w-5 h-5" />}
              {overallStatus.status === 'in-progress' && <Clock className="w-5 h-5" />}
              {overallStatus.status === 'not-started' && <FileText className="w-5 h-5" />}
              <span className="font-semibold">{overallStatus.message}</span>
            </div>
          </div>
        </div>

        {/* Overall Status Card */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Verification Progress</h2>
                <p className="text-gray-600">Your profile verification status</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">{verification_percentage}%</div>
              <div className="text-sm text-gray-500">Complete</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div 
              className="bg-blue-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${verification_percentage}%` }}
            ></div>
          </div>

          {/* Verification Areas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <VerificationArea 
              label="Identity Verification"
              verified={identity_verified}
            />
            <VerificationArea 
              label="License Verification"
              verified={licenses_verified}
            />
            <VerificationArea 
              label="Employment History"
              verified={employment_verified}
            />
            <VerificationArea 
              label="Education Background"
              verified={education_verified}
            />
          </div>
        </div>

        {/* Documents Status - Single Column Layout */}
        <div className="space-y-6">
          {/* <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <primaryAction.icon className="w-5 h-5 text-gray-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Next Steps</h3>
                  <p className="text-sm text-gray-600">
                    {overallStatus.status === 'verified' 
                      ? 'View your verified documents and profile'
                      : overallStatus.status === 'rejected'
                      ? 'Some documents need your attention'
                      : overallStatus.status === 'pending'
                      ? 'Documents are under review. You can upload additional documents.'
                      : 'Start the verification process by uploading your documents'
                    }
                  </p>
                </div>
              </div>
              <Button
                variant={primaryAction.variant}
                onClick={primaryAction.onClick}
                className="whitespace-nowrap"
              >
                <primaryAction.icon className="w-4 h-4 mr-2" />
                {primaryAction.label}
              </Button>
            </div>
          </div> */}

          {/* Documents Summary */}
          {(verified_documents.length > 0 || pending_documents.length > 0 || rejected_documents.length > 0) && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Documents Summary</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Verified Summary */}
                {verified_documents.length > 0 && (
                  <div 
                    className="border border-green-200 rounded-lg p-4 bg-green-50 cursor-pointer hover:bg-green-100 transition-colors"
                    onClick={() => navigate('/manage-profile?tab=documents')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-900">Verified</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-green-900 mt-2">{verified_documents.length}</p>
                    <p className="text-sm text-green-700">documents approved</p>
                  </div>
                )}

                {/* Pending Summary */}
                {pending_documents.length > 0 && (
                  <div 
                    className="border border-yellow-200 rounded-lg p-4 bg-yellow-50 cursor-pointer hover:bg-yellow-100 transition-colors"
                    onClick={() => navigate('/manage-profile?tab=documents')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-yellow-600" />
                        <span className="font-medium text-yellow-900">Pending</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-yellow-600" />
                    </div>
                    <p className="text-2xl font-bold text-yellow-900 mt-2">{pending_documents.length}</p>
                    <p className="text-sm text-yellow-700">under review</p>
                  </div>
                )}

                {/* Rejected Summary */}
                {rejected_documents.length > 0 && (
                  <div 
                    className="border border-red-200 rounded-lg p-4 bg-red-50 cursor-pointer hover:bg-red-100 transition-colors"
                    onClick={() => navigate('/manage-profile?tab=documents')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        <span className="font-medium text-red-900">Attention Needed</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-red-600" />
                    </div>
                    <p className="text-2xl font-bold text-red-900 mt-2">{rejected_documents.length}</p>
                    <p className="text-sm text-red-700">require action</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Detailed Documents Cards */}
          {(rejected_documents.length > 0 || pending_documents.length > 0 || verified_documents.length > 0) && (
            <div className="grid grid-cols-1 gap-6">
              {/* Verified Documents */}
              {verified_documents.length > 0 && (
                <DocumentStatusCard
                  title="Verified Documents"
                  documents={verified_documents}
                  status="verified"
                  icon={CheckCircle}
                />
              )}

              {/* Pending Documents */}
              {pending_documents.length > 0 && (
                <DocumentStatusCard
                  title="Pending Review"
                  documents={pending_documents}
                  status="pending"
                  icon={Clock}
                />
              )}

              {/* Rejected Documents */}
              {rejected_documents.length > 0 && (
                <DocumentStatusCard
                  title="Needs Attention"
                  documents={rejected_documents}
                  status="rejected"
                  icon={AlertCircle}
                />
              )}
            </div>
          )}

          {/* No Documents State */}
          {documentVerifications.length === 0 && (
            <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Documents Uploaded</h3>
              <p className="text-gray-600 mb-4">Start the verification process by uploading your documents</p>
              <Button
                onClick={() => navigate('/manage-profile?tab=documents')}
                className="whitespace-nowrap"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Documents
              </Button>
            </div>
          )}
        </div>

        {/* Last Verification Info */}
        {last_verification_date && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Last Verification</p>
                  <p className="text-sm text-blue-700">
                    {new Date(last_verification_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationStatusPage;